import { useMemo } from "react";
import intl from "react-intl-universal";
import { TbLock } from "react-icons/tb";
import { CurrencyIcon } from "../../../../../Components/Atoms/CurrencyIcon";
import {
  buildBaasSwapAssetsFromWallets,
  buildBaasWalletStablesAsset,
} from "../../../../../Services/baasStablesWalletApi";
import { formatCurrency } from "../../../../../Utils/formatters";
import { formatBrl, formatStable } from "../../../Stables/constants";

/**
 * Stables operation balance strip.
 * - Buy/sell/withdraw/deposit: selected wallet available/blocked (+ fiat BRL on buy).
 * - Swap: Total em BRL + one card per owned wallet asset (stable order; not tied to De/Para).
 */
export const BaasStablesOperationBalanceCards = ({
  wallet,
  wallets = [],
  operation,
  marketQuotes = [],
  /** Linked fiat BRL available in centavos. */
  brlBalance = 0,
  brlBalanceFormatted,
  loadingBalance = false,
}) => {
  const selectedAsset = useMemo(
    () => (wallet ? buildBaasWalletStablesAsset(wallet, marketQuotes) : null),
    [wallet, marketQuotes],
  );

  const swapAssets = useMemo(() => {
    if (operation !== "swap" || !wallet) return [];
    return buildBaasSwapAssetsFromWallets(wallets, marketQuotes, wallet, {
      pinSelected: false,
    });
    // Scope by entity/account — not by De/Para wallet id — so convert pickers never reshuffle cards.
  }, [
    operation,
    wallets,
    marketQuotes,
    wallet?.entityId,
    wallet?.accountId,
  ]);

  const swapTotalBrl = useMemo(
    () =>
      swapAssets.reduce(
        (sum, asset) =>
          sum + (Number(asset.balance) || 0) * (Number(asset.rate) || 0),
        0,
      ),
    [swapAssets],
  );

  if (!wallet || !selectedAsset) return null;

  if (operation === "swap") {
    const assets = swapAssets.length ? swapAssets : [selectedAsset];
    const totalBrl =
      swapAssets.length > 0
        ? swapTotalBrl
        : (Number(selectedAsset.balance) || 0) * (Number(selectedAsset.rate) || 0);

    return (
      <section className="mb-6 min-w-0">
        <h2 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.stables.operation.balanceSection")}
        </h2>

        <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(11.5rem,1fr))]">
          <div className="summary-card min-w-0">
            <div className="flex items-center gap-2">
              <CurrencyIcon asset="brl" />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("stables.totalBrl")}
              </p>
            </div>
            <p className="mt-2 text-xl font-bold text-brand-dark dark:text-gray-100">
              {formatBrl(totalBrl)}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {intl.get("stables.stablecoinsEquivalent")}
            </p>
          </div>

          {assets.map((asset) => (
            <div key={asset.id} className="summary-card min-w-0">
              <div className="flex items-center gap-2">
                <CurrencyIcon asset={asset.id} iconUrl={asset.iconUrl || null} />
                <p className="text-xs text-gray-500 dark:text-gray-400">{asset.label}</p>
              </div>
              <p className="mt-2 text-xl font-bold text-brand-dark dark:text-gray-100">
                {formatStable(asset.balance, asset.id)}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                ≈ {formatBrl((Number(asset.balance) || 0) * (Number(asset.rate) || 0))}
              </p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const balances = wallet.balances ?? {};
  const available = balances.available ?? 0;
  const blocked = balances.blocked ?? 0;
  const brlEquivalent = available * (selectedAsset.rate ?? 0);
  const brlDisplay = brlBalanceFormatted || formatCurrency(brlBalance || 0);
  const showFiatBrl = operation === "buy";

  return (
    <section className="mb-6 min-w-0">
      <h2 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">
        {intl.get("baas.stables.operation.balanceSection")}
      </h2>

      <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(11.5rem,1fr))]">
        <div className="summary-card min-w-0">
          <div className="flex items-center gap-2">
            <CurrencyIcon
              asset={selectedAsset.id}
              iconUrl={selectedAsset.iconUrl || wallet?.iconUrl || null}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {intl.get("baas.stables.operation.balanceWalletAvailable", {
                asset: wallet.asset,
              })}
            </p>
          </div>
          <p className="mt-2 text-xl font-bold text-brand-dark dark:text-gray-100">
            {formatStable(available, selectedAsset.id)}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            ≈ {formatBrl(brlEquivalent)}
          </p>
        </div>

        {showFiatBrl ? (
          <div className="summary-card min-w-0">
            <div className="flex items-center gap-2">
              <CurrencyIcon asset="brl" />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.stables.operation.brlFiatAvailable")}
              </p>
            </div>
            <p className="mt-2 text-xl font-bold text-brand-dark dark:text-gray-100">
              {loadingBalance ? "…" : brlDisplay}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {intl.get("baas.stables.operation.balanceAccountHint", {
                account: wallet.accountAlias || wallet.accountNumber || "—",
              })}
            </p>
          </div>
        ) : null}

        {blocked > 0 ? (
          <div className="summary-card min-w-0">
            <div className="flex items-center gap-2">
              <TbLock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {intl.get("baas.processing.blocked")}
              </p>
            </div>
            <p className="mt-2 text-xl font-bold text-brand-dark dark:text-gray-100">
              {formatStable(blocked, selectedAsset.id)}
            </p>
            <p className="mt-1 text-xs text-gray-400">{wallet.alias}</p>
          </div>
        ) : null}
      </div>
    </section>
  );
};
